import { type BusinessDayResource, BusinessDaysController_list_v1 } from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { useSession } from '@/auth/session';
import { type Clock, systemClock } from '@/lib/clock';

import { decideAccess } from '../access';
import { paths } from '../paths';
import { addDays, currentBusinessDate } from '../reporting/reporting-period';
import { formatBusinessDate } from '../tickets/business-date';
import { dayCloseAccess } from './access';

/** What the warning says up to its link, or `null` when no earlier day is open. */
function openDaysMessage(days: readonly BusinessDayResource[], today: string): string | null {
  const outletsByDate = new Map<string, Set<string>>();

  // `today` is excluded here as well as by the query's `to`: today's open day is
  // still trading and is never a reason to warn.
  for (const day of days) {
    if (day.state !== 'OPEN' || day.businessDate >= today) continue;

    const outlets = outletsByDate.get(day.businessDate) ?? new Set<string>();
    outlets.add(day.outletId);
    outletsByDate.set(day.businessDate, outlets);
  }

  // `YYYY-MM-DD` sorts as text in date order.
  const [oldest] = [...outletsByDate.keys()].sort();

  if (oldest === undefined) return null;

  if (outletsByDate.size > 1) {
    return `${String(outletsByDate.size)} earlier days are still open, the oldest ${formatBusinessDate(oldest)}. Close them in`;
  }

  const outlets = outletsByDate.get(oldest)?.size ?? 0;

  return `${formatBusinessDate(oldest)} is still open at ${String(outlets)} ${outlets === 1 ? 'outlet' : 'outlets'}. Close it in`;
}

/**
 * A line across the top of every back-office screen while a trading day before
 * today is still open anywhere the signed-in person can see (#335).
 *
 * Closing a day needs a counted drawer, and the longer it stays open the less
 * anyone can count — so a day nobody noticed is the failure this exists to end.
 *
 * Shown to exactly the people Day close admits, decided by that route's own
 * declaration, and not even asked for by anyone else. Re-read on navigation,
 * not on a timer, like `RefusedWritesLink`. A failed read draws nothing and is
 * logged: an alarm about the alarm is worse than the missing line.
 */
export function OpenDaysWarning({ clock = systemClock }: { clock?: Clock }) {
  const client = useApiClient();
  const { session } = useSession();
  const { pathname } = useLocation();
  const [message, setMessage] = useState<string | null>(null);

  const admitted =
    decideAccess(
      dayCloseAccess,
      session === null
        ? null
        : { role: session.user.role, mustChangeCredential: session.user.mustChangeCredential },
    ) === 'render';

  useEffect(() => {
    if (!admitted) return;

    const controller = new AbortController();
    const today = currentBusinessDate(clock);

    // `collectPages` reads every page, so a long-unnoticed backlog is counted
    // whole rather than stopping at the route's default fifty rows.
    collectPages<BusinessDayResource>((offset, limit) =>
      client.request(BusinessDaysController_list_v1, {
        query: { state: 'OPEN', to: addDays(today, -1), offset, limit },
        signal: controller.signal,
      }),
    )
      .then((days) => {
        if (!controller.signal.aborted) setMessage(openDaysMessage(days, today));
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;

        console.error('day close: the open business days could not be read', error);
        setMessage(null);
      });

    return () => {
      controller.abort();
    };
  }, [admitted, client, clock, pathname]);

  if (!admitted || message === null) return null;

  return (
    <div className="shrink-0 px-4 pt-3 lg:px-7">
      <Alert>
        <IconAlertTriangle />
        <AlertDescription>
          <p>
            {message}{' '}
            <Link to={paths.dayClose} className="font-medium underline underline-offset-3">
              Day close
            </Link>
            .
          </p>
        </AlertDescription>
      </Alert>
    </div>
  );
}
